'use client';

import { AUTH, removeSessionTokens, setSessionTokens } from '@/api/fetch';
import { getLocalStorageWithoutParse } from '@/utils/local-storage';
import { User, UserGroups } from '@/utils/types';
import { datadogRum } from '@datadog/browser-rum';
import { Context, createContext, useCallback, useEffect, useRef, useState } from 'react';

import {
  AuthSessionState,
  DEFAULT_AUTH_SESSION_STATE,
  SessionStatus,
  loadSessionWithRetry,
  reduceAuthSessionState,
  shouldClearSessionTokens,
} from './auth-session.utils';

interface IAuthContextProps {
  user: User | null;
  userLoaded: boolean;
  userData: User | null;
  userGroups: UserGroups[];
  sessionStatus: SessionStatus;
  setUser: (user: User) => void;
  setUserLoaded: (userLoaded: boolean) => void;
  setUserData: (userData: User | null) => void;
  setUserGroups: (userGroups: string[]) => void;
  clearAuthState: () => void;
  authState: any;
  setAuthState: (state: any) => void;
  loadUser: (options?: { preserveTokensOnError?: boolean }) => Promise<User | null>;
}

const AuthContext: Context<IAuthContextProps> = createContext({} as any);

type AuthState = AuthSessionState;

const DEFAULT_AUTH_STATE: AuthState = DEFAULT_AUTH_SESSION_STATE;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>({ ...DEFAULT_AUTH_STATE });
  // Compartilha a promise em voo em vez de devolver null: quem aguarda loadUser() precisa do usuário,
  // e um null por concorrência era indistinguível de "não autenticado".
  const inFlightRef = useRef<Promise<User | null> | null>(null);
  const hasLoadedRef = useRef(false);
  const sessionStatusRef = useRef<SessionStatus>('idle');

  const { user, userLoaded, userData, userGroups, sessionStatus } = authState;

  useEffect(() => {
    sessionStatusRef.current = sessionStatus;
  }, [sessionStatus]);

  const setUser = (user: User) => setAuthState((prevAuthState: AuthState) => ({ ...prevAuthState, user }));

  const setUserLoaded = (userLoaded: boolean) =>
    setAuthState((prevAuthState: AuthState) => ({ ...prevAuthState, userLoaded }));

  const setUserData = (userData: User | null) =>
    setAuthState((prevAuthState: AuthState) => ({ ...prevAuthState, userData }));

  const setUserGroups = (userGroups: string[]) =>
    setAuthState((prevAuthState: AuthState) => ({ ...prevAuthState, userGroups: userGroups as UserGroups[] }));

  const clearAuthState = () => {
    setAuthState({ ...DEFAULT_AUTH_STATE });
  };

  const loadUser = useCallback(
    (options?: { preserveTokensOnError?: boolean }): Promise<User | null> => {
      if (inFlightRef.current) return inFlightRef.current;

      const attempt = (async (): Promise<User | null> => {
        setAuthState((prevAuthState: AuthState) => ({ ...prevAuthState, sessionStatus: 'loading' }));

        const result = await loadSessionWithRetry({ token: getLocalStorageWithoutParse(AUTH.ACCESS_TOKEN) });

        if (result.status === 'authenticated') {
          setSessionTokens({ accessToken: 'authenticated', refreshToken: '' });
        } else if (shouldClearSessionTokens(result, options?.preserveTokensOnError)) {
          removeSessionTokens();
        }

        if (result.status === 'error') {
          console.warn('Session could not be resolved, keeping current tokens:', result.error);
        }

        setAuthState((prevAuthState: AuthState) => reduceAuthSessionState(prevAuthState, result));

        // Só um desfecho conclusivo sela a carga inicial — falha transitória tem que poder ser refeita.
        hasLoadedRef.current = result.status !== 'error';

        return result.status === 'authenticated' ? result.user : null;
      })();

      inFlightRef.current = attempt;
      return attempt.finally(() => {
        inFlightRef.current = null;
      });
    },
    [setAuthState]
  );

  // Auto-fetch once on mount, deferred to idle time to avoid blocking hydration
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => loadUser());
      } else {
        setTimeout(() => loadUser(), 100);
      }
    }
  }, [loadUser]);

  // Sem isto, uma falha de rede no mount deixava a sessão irresolvida até o usuário recarregar a página
  // — e consumidores que dependem de userData (ex.: ações restritas do backoffice) sumiam em silêncio.
  useEffect(() => {
    const retryIfUnresolved = () => {
      if (sessionStatusRef.current !== 'error') return;
      if (document.visibilityState === 'hidden') return;
      loadUser();
    };

    window.addEventListener('online', retryIfUnresolved);
    document.addEventListener('visibilitychange', retryIfUnresolved);

    return () => {
      window.removeEventListener('online', retryIfUnresolved);
      document.removeEventListener('visibilitychange', retryIfUnresolved);
    };
  }, [loadUser]);

  // Amarra a identidade do usuário logado ao RUM do Datadog, para localizar nos logs/session replay
  // a sessão do usuário com problema. Um único efeito cobre todos os fluxos de login/logout, já que
  // todos passam por authState. datadogRum é no-op silencioso se o RUM não tiver inicializado.
  useEffect(() => {
    const identity = userData ?? user;
    if (identity?.id) {
      datadogRum.setUser({
        id: identity.id,
        name: identity.name ?? undefined,
        email: identity.email ?? undefined,
        role: userGroups?.[0],
      });
    } else {
      datadogRum.clearUser();
    }
  }, [user, userData, userGroups]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        userLoaded,
        setUserLoaded,
        userData,
        setUserData,
        userGroups,
        setUserGroups,
        sessionStatus,
        authState,
        setAuthState,
        clearAuthState,
        loadUser,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
